import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Spinner } from '../components/ui/Spinner';
import { readAndClearSsoNonce, ssoErrorCopy } from './sso';

/**
 * SSO landing route (`/auth/callback`, SC-2621). After the Google round-trip the
 * BE redirects the browser here with the result in the URL *fragment* (so tokens
 * never hit a server or an access log):
 *   success: #access_token=<jwt>&refresh_token=<token>&client_nonce=<echo>
 *   failure: #error=<code>&client_nonce=<echo>   (not_provisioned | email_unverified | provider_error)
 *
 * Public route (sibling to /login, NOT behind ProtectedRoute) — the user isn't
 * authenticated in this app yet when they land here. On success `completeSsoLogin`
 * hydrates the session via /me and routes by role; on any failure we route back
 * to /login with the matching copy in router state.
 *
 * SC-2780 — before hydrating, the token path requires the fragment's
 * `client_nonce` to match the nonce this tab stashed in `beginSso`. An injected
 * `#access_token=…` fragment lands in a tab that never minted a nonce, so it has
 * nothing to match → rejected. Error/malformed paths carry no token payload, so
 * they don't gate on the nonce (but the stash is still cleared, single-use).
 */
export function AuthCallbackPage() {
  const { completeSsoLogin } = useAuth();
  const navigate = useNavigate();
  // StrictMode double-invokes effects in dev; the token round-trip must run
  // exactly once. (The fragment is scrubbed below, so a real re-run would find
  // nothing anyway, but the guard keeps the navigate/hydrate from firing twice.)
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const params = new URLSearchParams(window.location.hash.slice(1));
    const errorCode = params.get('error');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const fragmentNonce = params.get('client_nonce');
    // Pull-and-clear the stashed nonce on every path (single-use hygiene) so it
    // can't leak into a later flow, even when this callback isn't the token path.
    const expectedNonce = readAndClearSsoNonce();

    const bailToLogin = (code?: string | null) =>
      navigate('/login', { replace: true, state: { error: ssoErrorCopy(code) } });

    if (errorCode) {
      bailToLogin(errorCode);
      return;
    }
    if (!accessToken || !refreshToken) {
      // Malformed fragment (neither an error nor a token pair) — treat as a
      // generic failure rather than silently stranding on a blank spinner.
      bailToLogin(null);
      return;
    }
    // SC-2780 — bind the token-bearing callback to the tab that started the flow.
    // No stashed nonce (direct visit / injected fragment) or a mismatch → reject
    // WITHOUT hydrating. Must stay strict: accepting a nonce-less token fragment
    // would reopen the exact vector this closes. (Requires the SC-2780 BE echo to
    // be deployed in this environment first, or legitimate logins fail closed.)
    if (!expectedNonce || fragmentNonce !== expectedNonce) {
      bailToLogin(null);
      return;
    }

    // Scrub the tokens from the URL bar / history *before* the async /me hop so
    // they can't linger if hydration throws or the user hits back.
    window.history.replaceState(null, '', window.location.pathname);

    void completeSsoLogin(accessToken, refreshToken).catch(() => bailToLogin(null));
  }, [completeSsoLogin, navigate]);

  // completeSsoLogin navigates on success; until then (and during the redirect
  // to /login on failure) show a neutral full-screen loading state.
  return <Spinner fullscreen />;
}
